import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';

const SERVICE_TYPES = ['주일예배', '수요예배', '금요기도회', '새벽기도회'];

type AttendanceStatus = 'present' | 'online' | 'absent';

const STATUS_LABEL: Record<AttendanceStatus, string> = {
  present: '출석',
  online: '온라인',
  absent: '결석',
};

const STATUS_ORDER: AttendanceStatus[] = ['present', 'online', 'absent'];

type RosterRow = {
  id: number;
  name: string;
  position: string;
  department: string;
  status: AttendanceStatus;
};

function todayInputValue() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default function ManageAttendance({ authChurch }: { authChurch: string }) {
  const [date, setDate] = useState(todayInputValue());
  const [serviceType, setServiceType] = useState(SERVICE_TYPES[0]);
  const [rows, setRows] = useState<RosterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const fetchRoster = () => {
    if (!date || !serviceType) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${MainURL}/attendance/roster`, { params: { authChurch, date, serviceType } })
      .then((res) => setRows(res.data?.data ?? []))
      .catch((err) => {
        console.error('GET /attendance/roster', err);
        setError('출석부를 불러오지 못했습니다.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRoster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChurch, date, serviceType]);

  const setStatus = (row: RosterRow, status: AttendanceStatus) => {
    if (row.status === status) return;
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status } : r)));
    axios
      .put(`${MainURL}/attendance/toggle`, {
        authChurch,
        memberId: row.id,
        date,
        serviceType,
        status,
      })
      .catch((err) => {
        console.error('PUT /attendance/toggle', err);
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: row.status } : r)));
      });
  };

  const presentCount = rows.filter((r) => r.status === 'present').length;
  const onlineCount = rows.filter((r) => r.status === 'online').length;
  const absentCount = rows.filter((r) => r.status === 'absent').length;

  const filteredRows = useMemo(() => {
    const keyword = search.trim();
    if (!keyword) return rows;
    return rows.filter((row) => row.name.includes(keyword));
  }, [rows, search]);

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">출석부</h2>
          <p className="manage-panel-desc">예배별 출석을 체크하고 관리합니다.</p>
        </div>
      </div>

      <div className="manage-filterbar manage-attendance-toolbar">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <div className="manage-seg">
          {SERVICE_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              className={`manage-seg-btn${serviceType === type ? ' is-active' : ''}`}
              onClick={() => setServiceType(type)}
            >
              {type}
            </button>
          ))}
        </div>
        <div className="manage-filterbar-search">
          🔍
          <input
            type="text"
            placeholder="이름으로 검색"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading && <div className="manage-state">불러오는 중입니다...</div>}
      {!loading && error && <div className="manage-state manage-state--error">{error}</div>}

      {!loading && !error && (
        <div className="manage-grid-2col">
          <section className="manage-card-block">
            <div className="manage-block-head">
              <h3 className="manage-block-title">{serviceType} 출석 체크</h3>
              <span className="manage-badge manage-badge--green">출석 {presentCount}</span>
              <span className="manage-badge manage-badge--navy">재적 {rows.length}</span>
              <span className="manage-block-count">{date}</span>
            </div>
            <div className="manage-table-wrap">
              <table className="manage-table">
                <thead>
                  <tr>
                    <th>이름</th>
                    <th style={{ width: 210 }}>출석 상태</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={2} className="manage-table-empty">재적 성도가 없습니다. 교적관리에서 먼저 등록해 주세요.</td>
                    </tr>
                  )}
                  {rows.length > 0 && filteredRows.length === 0 && (
                    <tr>
                      <td colSpan={2} className="manage-table-empty">검색 결과가 없습니다.</td>
                    </tr>
                  )}
                  {filteredRows.map((row, index) => (
                    <tr
                      key={row.id}
                      className={`manage-attendance-row${row.status === 'present' ? ' is-present' : ''}${row.status === 'absent' ? ' is-absent' : ''}`}
                    >
                      <td>
                        <div className="manage-mcell">
                          <span className={`manage-avatar manage-avatar--${(index % 6) + 1}`}>{row.name.slice(0, 1)}</span>
                          <div>
                            <b>{row.name}</b>
                            <small>{[row.position, row.department].filter(Boolean).join(' · ')}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="manage-att-toggle">
                          {STATUS_ORDER.map((status) => (
                            <button
                              key={status}
                              type="button"
                              className={`manage-att-toggle-btn is-${status}${row.status === status ? ' is-active' : ''}`}
                              onClick={() => setStatus(row, status)}
                            >
                              {STATUS_LABEL[status]}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <aside className="manage-side-stack">
            <div className="manage-side-card">
              <div className="manage-side-card-head">현황</div>
              <div className="manage-side-card-body">
                <div className="manage-stat-row">
                  <span className="manage-stat-row-dot" style={{ background: 'var(--success)' }} />
                  <span className="manage-stat-row-label">출석</span>
                  <span className="manage-stat-row-count">{presentCount}명</span>
                </div>
                <div className="manage-stat-row">
                  <span className="manage-stat-row-dot" style={{ background: 'var(--info)' }} />
                  <span className="manage-stat-row-label">온라인</span>
                  <span className="manage-stat-row-count">{onlineCount}명</span>
                </div>
                <div className="manage-stat-row">
                  <span className="manage-stat-row-dot" style={{ background: 'var(--danger)' }} />
                  <span className="manage-stat-row-label">결석</span>
                  <span className="manage-stat-row-count">{absentCount}명</span>
                </div>
                <div className="manage-stat-row">
                  <span className="manage-stat-row-dot" style={{ background: 'var(--text-soft)' }} />
                  <span className="manage-stat-row-label">재적</span>
                  <span className="manage-stat-row-count">{rows.length}명</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
